import React, { FC, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { VersionT } from "../../models/models";
import Tooltip from "../../components/Tooltip/Tooltip";

import ProgressBar from "./components/ProgressBar/ProgressBar";
import TimeDisplay from "./components/TimeDisplay/TimeDisplay";
import playIcon from "./icons/play.svg";
import pauseIcon from "./icons/pause.svg";
import replayIcon from "./icons/replay.svg";
import { SafeZoneOverlay, SafeZoneButton } from "./components/SafeZone/SafeZone";
import SoundControl from "./components/SoundControl/SoundControl";
import fullscreenIcon from "./icons/fullscreen.svg";
import styles from "./videoPlayerCustom.module.scss";

const getFullscreenElement = () =>
    document.fullscreenElement ?? (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement;

const requestFullscreenSafe = (el: HTMLElement) => {
    const promise =
        el.requestFullscreen?.() ?? (el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen?.();
    if (promise instanceof Promise) promise.catch(() => { });
};

const exitFullscreenSafe = () => {
    const doc = document as Document & { exitFullscreen?: () => Promise<void>; webkitExitFullscreen?: () => Promise<void> };
    const promise = doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.();
    if (promise instanceof Promise) promise.catch(() => { });
};

type VideoPlayerCustomProps = {
    item: VersionT;
};

const VideoPlayerCustom: FC<VideoPlayerCustomProps> = ({ item }) => {
    const playerRef = useRef<ReactPlayer>(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const url = item.DesktopPlaybackUrl ?? "";

    const [playing, setPlaying] = useState(true);
    const [muted, setMuted] = useState(false);
    const [volume, setVolume] = useState(0.8);
    const [currentTimeMs, setCurrentTimeMs] = useState(0);
    const [seeking, setSeeking] = useState(false);
    const [duration, setDuration] = useState(0);
    const [ended, setEnded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isSafeZoneVisible, setIsSafeZoneVisible] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!getFullscreenElement());
        };
        document.addEventListener("fullscreenchange", handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
        };
    }, []);

    useEffect(() => {
        if (!playing || seeking) return;
        let rafId: number;
        const tick = () => {
            const player = playerRef.current;
            const current = player?.getCurrentTime() ?? 0;
            const dur = player?.getDuration();
            if (typeof dur === "number" && dur > 0) {
                setCurrentTimeMs(Math.round(current * 1000));
            }
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [playing, seeking]);

    const handlePlayPause = () => setPlaying((prev) => !prev);
    const handleReplay = () => {
        setEnded(false);
        setCurrentTimeMs(0);
        playerRef.current?.seekTo(0);
        setPlaying(true);
    };
    const handleMute = () => setMuted((prev) => !prev);
    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVolume(parseFloat(e.target.value));
        setMuted(e.target.value === "0");
    };

    const handleProgress = (state: { played: number }) => {
        if (!seeking) {
            setCurrentTimeMs(Math.round(duration * state.played * 1000));
        }
    };

    const handleSeekMouseDown = () => setSeeking(true);

    const handleDuration = (dur: number) => {
        setDuration(dur);
        setCurrentTimeMs((prev) =>
            dur > 0 ? Math.min(prev, Math.round(dur * 1000)) : prev
        );
    };

    const handleEnded = () => {
        setPlaying(false);
        setEnded(true);
    };

    const handleVideoClick = () => {
        if (ended) {
            handleReplay();
        } else {
            handlePlayPause();
        }
    };

    const handleFullscreenClick = () => {
        if (getFullscreenElement()) {
            exitFullscreenSafe();
        } else {
            const el = playerContainerRef.current;
            if (el) requestFullscreenSafe(el);
        }
    };

    return (
        <div className={styles.wrapper}>
            <div
                ref={playerContainerRef}
                className={styles.playerContainer}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <ReactPlayer
                    ref={playerRef}
                    url={url}
                    playing={playing}
                    muted={muted}
                    volume={volume}
                    width="100%"
                    height="100%"
                    onProgress={handleProgress}
                    onDuration={handleDuration}
                    onEnded={handleEnded}
                    controls={false}
                />
                <div
                    className={styles.videoClickOverlay}
                    onClick={handleVideoClick}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleVideoClick();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={ended ? "Replay" : playing ? "Pause" : "Play"}
                />
                <SafeZoneOverlay visible={isSafeZoneVisible} />
                <div
                    className={`${styles.centerIconOverlay} ${isHovered && !playing ? styles.centerIconOverlayVisible : ""}`}
                    aria-hidden
                >
                    {ended ? (
                        <img src={replayIcon} alt="" className={styles.centerIcon} />
                    ) : (
                        <img src={playIcon} alt="" className={styles.centerIcon} />
                    )}
                </div>
                <div
                    className={`${styles.controlsOverlay} ${isHovered ? styles.controlsOverlayVisible : ""}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.controlsRow}>
                        <Tooltip
                            text={ended ? "Replay" : playing ? "Pause" : "Play"}
                            withoutTranslation
                        >
                            <button
                                type="button"
                                className={`${styles.playButton} ${playing && !ended ? styles.playButtonPause : ""}`}
                                onClick={ended ? handleReplay : handlePlayPause}
                                aria-label={ended ? "Replay" : playing ? "Pause" : "Play"}
                            >
                                {ended ? (
                                    <img src={replayIcon} alt="" className={styles.playIcon} />
                                ) : playing ? (
                                    <img src={pauseIcon} alt="" className={styles.playIcon} />
                                ) : (
                                    <img src={playIcon} alt="" className={styles.playIcon} />
                                )}
                            </button>
                        </Tooltip>
                        <SoundControl
                            muted={muted}
                            volume={volume}
                            onMuteToggle={handleMute}
                            onVolumeChange={handleVolumeChange}
                        />
                        <TimeDisplay
                            currentTime={currentTimeMs / 1000}
                            duration={duration}
                        />
                        <SafeZoneButton
                            isVisible={isSafeZoneVisible}
                            onToggle={() => setIsSafeZoneVisible((v) => !v)}
                        />
                        <Tooltip
                            text={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                            withoutTranslation
                        >
                            <button
                                type="button"
                                className={styles.fullscreenButton}
                                onClick={handleFullscreenClick}
                                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                            >
                                <img src={fullscreenIcon} alt="" className={styles.fullscreenIcon} />
                            </button>
                        </Tooltip>
                    </div>
                </div>
                <div
                    className={styles.progressBarWrap}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ProgressBar
                        currentTime={currentTimeMs}
                        duration={duration * 1000}
                        onSeekChange={(value) => {
                            setCurrentTimeMs(Math.round(value * duration * 1000));
                        }}
                        onSeekStart={handleSeekMouseDown}
                        onSeekEnd={(value) => {
                            setSeeking(false);
                            playerRef.current?.seekTo(value);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default VideoPlayerCustom;